// Path resolution for the dev-only catalogue writer — the security core,
// deliberately extracted as a PURE function so its guarantees can be proven
// by test rather than asserted in a code review of request-handler plumbing.
//
// The threat model is modest but real: a dev server listening on localhost is
// reachable by any page in the developer's browser and by anything running on
// their machine. A writer endpoint that takes an id and produces a path is a
// classic arbitrary-file-write primitive if it is careless. So:
//
//   * the collection must be one of a fixed allow-list — not caller-supplied
//     path fragments;
//   * the id must satisfy the domain's own id rule BEFORE any path is built;
//   * and the resolved absolute path must still be inside the allow-listed
//     directory afterwards — the belt-and-braces check that also defeats
//     anything the id rule failed to anticipate.
//
// The last check is the important one: the first two are validation, but only
// resolving and re-checking survives a mistake in them.

import { isValidDocumentId, type StoredDocument } from './documentSchema';

/**
 * The only directories the writer may touch, by document kind.
 *
 * They live under their own `catalogue/` root deliberately. `src/data/scores`
 * already holds Score Studio's native `*.vlscore.json` documents — a
 * different schema with its own record/revision model — and pointing the
 * writer at a shared directory would mean two document systems in one folder,
 * distinguishable only by filename convention. One root, one schema.
 */
export const CATALOGUE_ROOT = 'src/data/catalogue';

export const CATALOGUE_DIRS = {
  score: `${CATALOGUE_ROOT}/scores`,
  arrangement: `${CATALOGUE_ROOT}/arrangements`,
  groove: `${CATALOGUE_ROOT}/grooves`,
  media: `${CATALOGUE_ROOT}/media`,
} as const satisfies Record<StoredDocument['kind'], string>;

/** Which kind a catalogue directory holds, by its last path segment. */
export function kindOfDirectory(segment: string): CatalogueKind | undefined {
  for (const kind of Object.keys(CATALOGUE_DIRS) as CatalogueKind[]) {
    if (CATALOGUE_DIRS[kind].endsWith(`/${segment}`)) return kind;
  }
  return undefined;
}

export type CatalogueKind = keyof typeof CATALOGUE_DIRS;

export function isCatalogueKind(value: unknown): value is CatalogueKind {
  return typeof value === 'string' && Object.hasOwn(CATALOGUE_DIRS, value);
}

export type PathResolution =
  | { ok: true; relativePath: string }
  | { ok: false; reason: string };

/**
 * Resolve `kind` + `id` to a repository-relative file path, or refuse.
 *
 * Pure and platform-independent: it normalises with POSIX semantics and never
 * touches the filesystem, so it can be exhaustively tested. The caller joins
 * the result onto the project root and — critically — re-checks containment
 * against the REAL path (see `resolveInsideRoot`), which is what catches a
 * symlink pointing out of the tree.
 */
export function resolveCataloguePath(kind: unknown, id: unknown): PathResolution {
  if (!isCatalogueKind(kind)) {
    return { ok: false, reason: `unknown collection: ${JSON.stringify(kind)}` };
  }
  if (typeof id !== 'string' || !isValidDocumentId(id)) {
    return { ok: false, reason: `unsafe or malformed document id: ${JSON.stringify(id)}` };
  }
  const dir = CATALOGUE_DIRS[kind];
  const relativePath = `${dir}/${id}.json`;

  // Re-verify after composing: normalising must not have escaped the
  // allow-listed directory. Belt and braces over the id rule above.
  const normalised = normalisePosix(relativePath);
  if (normalised !== relativePath || !normalised.startsWith(`${dir}/`)) {
    return { ok: false, reason: 'resolved path escaped its collection directory' };
  }
  return { ok: true, relativePath: normalised };
}

/** Collapse `.` / `..` / duplicate separators using POSIX rules. */
export function normalisePosix(path: string): string {
  const absolute = path.startsWith('/');
  const out: string[] = [];
  for (const part of path.split('/')) {
    if (part === '' || part === '.') continue;
    if (part === '..') {
      if (out.length && out[out.length - 1] !== '..') out.pop();
      else if (!absolute) out.push('..');
      continue;
    }
    out.push(part);
  }
  return (absolute ? '/' : '') + out.join('/');
}

/**
 * Is `candidate` genuinely inside `root`? Both must already be absolute and
 * fully resolved (symlinks followed) by the caller — this is the containment
 * predicate, not the resolver.
 */
export function isInsideRoot(root: string, candidate: string): boolean {
  const r = normalisePosix(root).replace(/\/$/, '');
  const c = normalisePosix(candidate);
  return c === r || c.startsWith(`${r}/`);
}
